import { type FC, type ReactNode } from "react";
import { useNavigate } from "react-router";

import {
  type DashboardProjectsListQuery,
  type ProjectFragment,
} from "@app_schema";

import { track } from "@application/utilities/track";

import { Avatar } from "@styled/avatar";
import { BasicLink } from "@styled/basic_link";
import { Button } from "@styled/button";
import {
  CaseCard,
  CaseCardMore,
  type CaseStep,
  CaseStepper,
} from "@styled/case_card";

import {
  type CaseStepKey,
  SECTION_PARAM,
  caseSteps,
} from "./case_status_lines";
import { evalTypeLabel } from "./eval_type_badge";
import { formatDob } from "./format_dob";
import { ProjectMenu } from "./project_menu";
import { type SummaryCategory } from "./summary_count_bar";

export type ProjectInboxEntry =
  DashboardProjectsListQuery["projects"]["entries"][number];

// Status is one strip of steps under the name: Records · Intake · Dictation ·
// Drafting · Report, Intake and Drafting only while the org's flags are on.
// The row's one next step is a tinted button with •••, none while processing.
export const ProjectInboxCard: FC<{
  project: ProjectInboxEntry;
  category: SummaryCategory;
}> = ({ project, category }) => {
  const navigate = useNavigate();
  const detailPath = `/dashboard/projects/${project.id}`;
  const pathOf = (key: CaseStepKey) =>
    `${detailPath}?section=${SECTION_PARAM[key]}`;
  const lines = caseSteps(project);

  // The Records step's title carries the case's counts after its state: pages
  // and letters once records are processed (each left out at 0), else how
  // many files are in.
  const recordsCounts: string[] = [];
  if (category === "readyForReview") {
    if (project.pagesCount > 0) {
      recordsCounts.push(`${project.pagesCount.toLocaleString()} pages`);
    }
    if (project.lettersCount > 0) {
      recordsCounts.push(`${project.lettersCount.toLocaleString()} letters`);
    }
  } else if (project.sourceFilesCount > 0) {
    recordsCounts.push(
      `${project.sourceFilesCount.toLocaleString()} ${project.sourceFilesCount === 1 ? "file" : "files"}`,
    );
  }

  const steps: CaseStep[] = lines.map((line) => ({
    to: pathOf(line.key),
    label: line.short,
    tone: line.tone,
    title:
      line.key === "records"
        ? [line.title, ...recordsCounts].join(" · ")
        : line.title,
  }));
  const toneOf = (key: CaseStepKey) =>
    lines.find((line) => line.key === key)?.tone;
  const empty = project.documentsCount === 0;

  const goTo = (action: string, path: string) => () => {
    track("project_action_clicked", { project_id: project.id, action });
    navigate(path);
  };

  // After the name: the DOB, then the eval type.
  const facts: ReactNode[] = [];
  if (project.claimantDob) {
    facts.push(`DOB ${formatDob(project.claimantDob)}`);
  }
  if (project.evaluationType) {
    facts.push(evalTypeLabel(project.evaluationType));
  }

  const nextStep = (): ReactNode => {
    if (toneOf("records") === "failed") {
      return (
        <NextStep onClick={goTo("see_failed", pathOf("records"))}>
          See what failed
        </NextStep>
      );
    }
    if (toneOf("finalReport") === "failed") {
      return (
        <NextStep onClick={goTo("see_failed", pathOf("finalReport"))}>
          See what failed
        </NextStep>
      );
    }
    if (category === "processing") return null;
    if (toneOf("finalReport") === "done") {
      return (
        <NextStep onClick={goTo("evaluate_report", pathOf("finalReport"))}>
          Evaluate report
        </NextStep>
      );
    }
    if (category === "needsUpload") {
      return (
        <NextStep onClick={goTo("upload_records", detailPath)}>
          Upload records
        </NextStep>
      );
    }
    if (project.recordsReviewReady) {
      return (
        <NextStep onClick={goTo("review_records", detailPath)}>
          Review records
        </NextStep>
      );
    }
    return (
      <NextStep onClick={goTo("evaluate_report", pathOf("finalReport"))}>
        Evaluate report
      </NextStep>
    );
  };

  return (
    <CaseCard
      empty={empty}
      avatar={<Avatar user={project.user} />}
      title={
        <BasicLink
          to={detailPath}
          tone="title"
          onClick={() => track("project_clicked", { project_id: project.id })}
        >
          {project.name}
        </BasicLink>
      }
      facts={facts}
      status={<CaseStepper steps={steps} />}
      actions={
        <>
          {nextStep()}
          <CaseCardMore>
            <ProjectMenu project={project as ProjectFragment} />
          </CaseCardMore>
        </>
      }
    />
  );
};

const NextStep: FC<{ onClick(): void; children: ReactNode }> = ({
  onClick,
  children,
}) => (
  <Button type="button" color="tint" size="sm" onClick={onClick}>
    {children}
  </Button>
);
